import {
  useMediaQuery,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import AdminSidebar from "./AdminSidebar";
import { Search } from "@mui/icons-material";
import color from "../../components/utils/Colors";
import { useEffect, useMemo, useState } from "react";
import EntityList from "../../components/cards/EntityList";
import {
  AdminEntity,
  countEntitiesByTab,
  fetchTeachers,
  filterEntitiesByTab,
} from "../../services/adminData";

const AdminTeacher = () => {
  const isSmallScreen = useMediaQuery("(max-width:1050px)");
  const [active, setActive] = useState("New Joined");
  const [searchQuery, setSearchQuery] = useState("");
  const [teachers, setTeachers] = useState<AdminEntity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchTeachers()
      .then(setTeachers)
      .finally(() => setLoading(false));
  }, []);

  const tabCounts = useMemo(() => countEntitiesByTab(teachers), [teachers]);

  const buttons = [
    { label: "New Joined", count: tabCounts["New Joined"] },
    { label: "Verified", count: tabCounts.Verified },
    { label: "Archived", count: tabCounts.Archived },
  ];

  const filteredTeachers = useMemo(() => {
    const byTab = filterEntitiesByTab(teachers, active);
    if (!searchQuery.trim()) return byTab;
    const query = searchQuery.toLowerCase();
    return byTab.filter(
      (teacher) =>
        teacher.name.toLowerCase().includes(query) ||
        teacher.email.toLowerCase().includes(query) ||
        teacher.contact.toLowerCase().includes(query),
    );
  }, [teachers, active, searchQuery]);

  return (
    <Box
      sx={{
        display: "flex",
        backgroundImage: "url('/images/logo 2.svg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        height: "auto",
        margin: isSmallScreen ? "10px" : "30px",
        marginTop: "0px",
        paddingTop: "20px",
        gap: "3%",
        borderRadius: "10px",
        flexDirection: isSmallScreen ? "column" : "row",
      }}
    >
      <AdminSidebar />

      <Box sx={{ flex: 1, borderRadius: "16px" }}>
        <TextField
          className="input-root2"
          placeholder="Search..."
          size="small"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          InputProps={{
            startAdornment: (
              <IconButton>
                <Search />
              </IconButton>
            ),
          }}
          sx={{ width: { xs: "100%", md: "auto" } }}
        />

        <Box
          sx={{
            position: "relative",
            background: color.firstColor,
            borderRadius: "16px",
            height: "fit-content",
            my: 4,
          }}
        >
          <Box
            component="img"
            src="/assets/Welcome Bar.svg"
            alt="Welcome Bar"
            sx={{ width: "100%", height: 200, objectFit: "contain" }}
          />
          <Typography
            sx={{
              color: "white",
              position: "absolute",
              top: { xs: "15%", sm: "25%" },
              left: "3%",
              fontSize: "22px",
              fontWeight: "bold",
            }}
          >
            Teachers
          </Typography>
          <Typography
            sx={{
              color: "white",
              position: "absolute",
              left: "3%",
              maxWidth: "62%",
              top: { xs: "34%", sm: "50%" },
            }}
          >
            {teachers.length} teacher{teachers.length === 1 ? "" : "s"} on the
            platform. {tabCounts.Verified} verified with published courses.
          </Typography>
        </Box>

        <Box
          sx={{
            my: 4,
            mt: 2,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 2, sm: 4 },
            boxShadow: "none",
          }}
        >
          {buttons.map((button) => (
            <Button
              key={button.label}
              onClick={() => setActive(button.label)}
              sx={{
                backgroundColor:
                  active === button.label ? color.firstColor : "#D3D3D3",
                color: active === button.label ? "white" : color.firstColor,
                border: "solid 1px white",
                "&:hover": {
                  backgroundColor: color.firstColor,
                  color: "white",
                },
                minWidth: "200px",
                textTransform: "none",
                display: "flex",
                gap: 2,
                borderRadius: 2,
                py: 2,
                fontWeight: "bold",
              }}
            >
              {button.label} - {button.count}
            </Button>
          ))}
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress sx={{ color: color.firstColor }} />
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <EntityList entities={filteredTeachers} listName="Teacher" />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AdminTeacher;
